import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { FileText, Clock, CheckCircle, Plus, Edit, Trash2, Loader2, AlertTriangle, Calendar, Eye, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import PaymentModal from '../../components/payment/PaymentModal';
import RenewalPaymentModal from '../../components/payment/RenewalPaymentModal';
import DraftLimitModal from '../../components/will/DraftLimitModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientWill = () => {
  const outletContext = useOutletContext() || {};
  const { subdomain: contextSubdomain, useSubdomainRouting, isEmbedded = false, embedPartnerId } = outletContext;
  const { partnerId: urlPartnerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // For embedded mode, get subdomain from localStorage
  const subdomain = isEmbedded ? localStorage.getItem('clientSubdomain') : contextSubdomain;
  const partnerId = embedPartnerId || urlPartnerId;
  
  const navigate = useNavigate();
  const clientName = localStorage.getItem('clientName') || 'Client';
  
  const [wills, setWills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [showDraftLimitModal, setShowDraftLimitModal] = useState(false);
  const [renewalWill, setRenewalWill] = useState(null);
  const [partnerPricing, setPartnerPricing] = useState(null); // Pre-fetched pricing
  const [maxUnpaidDrafts, setMaxUnpaidDrafts] = useState(10); // Admin-configurable limit

  // Helper function to get correct URL based on routing mode
  const getUrl = (path) => {
    if (isEmbedded && partnerId) {
      return `/embed/${partnerId}${path}`;
    }
    return path;
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('clientToken');
    return { Authorization: `Bearer ${token}` };
  };

  // Handle payment success redirect from Razorpay
  useEffect(() => {
    const paymentStatus = searchParams.get('payment_status');
    const paymentType = searchParams.get('type');
    
    if (paymentStatus === 'success') {
      // Small delay to ensure Toaster is ready after redirect
      const timer = setTimeout(() => {
        // Show success toast based on payment type
        if (paymentType === 'purchase') {
          toast.success('Payment successful! Your will has been created. You can now start editing.', {
            duration: 6000,
          });
        } else if (paymentType === 'renewal') {
          toast.success('Payment successful! Your will access has been renewed.', {
            duration: 6000,
          });
        } else {
          toast.success('Payment completed successfully!', {
            duration: 6000,
          });
        }
        
        // Clear the query params AFTER showing toast
        setSearchParams({});
        
        // Refresh wills list
        fetchWills();
      }, 500);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchWills();
    fetchPartnerPricing(); // Pre-fetch pricing on page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPartnerPricing = async () => {
    try {
      const response = await axios.get(`${API}/client/partner-info`, {
        headers: getAuthHeader()
      });
      setPartnerPricing(response.data);
      // Get max unpaid drafts limit from partner settings
      if (response.data.max_unpaid_drafts) {
        setMaxUnpaidDrafts(response.data.max_unpaid_drafts);
      }
    } catch (error) {
      console.error('Error fetching partner pricing:', error);
      // Fallback pricing
      setPartnerPricing({ will_price: 999, renewal_price: 999 });
    }
  };

  const fetchWills = async () => {
    // Always reset state before fetching — prevents ghost wills from a previous session
    setWills([]);
    setLoading(true);
    try {
      const response = await axios.get(`${API}/client/wills`, {
        headers: getAuthHeader()
      });
      setWills(response.data.wills || []);
    } catch (error) {
      console.error('Error fetching wills:', error);
      toast.error('Failed to load wills');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWill = async () => {
    // Count unpaid draft wills
    const unpaidDrafts = wills.filter(w => !w.is_paid && w.status !== 'completed');
    
    if (unpaidDrafts.length >= maxUnpaidDrafts) {
      // Show draft limit modal
      setShowDraftLimitModal(true);
      return;
    }
    
    // Create draft will directly (no payment required upfront)
    setCreatingDraft(true);
    try {
      const response = await axios.post(
        `${API}/client/will/draft`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.success && response.data.will_id) {
        toast.success('Draft will created! Start filling in your details.');
        navigate(getUrl(`/will/create/${response.data.will_id}`));
      } else {
        toast.error('Failed to create draft will');
      }
    } catch (error) {
      console.error('Error creating draft will:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create draft will';
      toast.error(errorMessage);
    } finally {
      setCreatingDraft(false);
    }
  };

  const handleDeleteDraftFromLimit = async (willId) => {
    try {
      await axios.delete(`${API}/client/will/${willId}`, {
        headers: getAuthHeader()
      });
      toast.success('Draft deleted successfully');
      setShowDraftLimitModal(false);
      fetchWills();
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast.error('Failed to delete draft');
    }
  };

  const handlePaymentSuccess = (data) => {
    setShowPaymentModal(false);
    // Navigate to the will creation page with the new will ID
    // data contains { success, message, payment_id, will_id, access_expiry_date }
    const willId = data?.will_id;
    if (willId) {
      navigate(getUrl(`/will/create/${willId}`));
    } else {
      // Fallback: refresh the will list
      fetchWills();
    }
  };

  const handleRenewWill = (will) => {
    setRenewalWill(will);
    setShowRenewalModal(true);
  };

  const handleRenewalSuccess = (data) => {
    setShowRenewalModal(false);
    setRenewalWill(null);
    toast.success(`Access extended! New expiry: ${new Date(data.new_expiry_date).toLocaleDateString()}`);
    // Refresh will list to show updated expiry
    fetchWills();
  };

  const handleEditWill = (willId) => {
    navigate(getUrl(`/will/create/${willId}`));
  };

  const handleDeleteWill = async (willId) => {
    if (!window.confirm('Are you sure you want to delete this Will? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/client/will/${willId}`, {
        headers: getAuthHeader()
      });
      toast.success('Will deleted successfully');
      fetchWills();
    } catch (error) {
      console.error('Error deleting will:', error);
      toast.error('Failed to delete will');
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return {
          icon: CheckCircle,
          color: 'emerald',
          bgColor: 'bg-emerald-500/10',
          borderColor: 'border-emerald-500/30',
          textColor: 'text-emerald-400',
          label: 'Completed'
        };
      case 'draft':
      default:
        return {
          icon: Clock,
          color: 'amber',
          bgColor: 'bg-amber-500/10',
          borderColor: 'border-amber-500/30',
          textColor: 'text-amber-400',
          label: 'Draft'
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="loading">
        <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
        <p className="text-slate-400">Loading your Wills...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="client-will">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">My Will</h1>
          <p className="text-slate-400 mt-1">Create and manage your legally valid will</p>
        </div>
        <Button
          onClick={handleCreateWill}
          disabled={creatingDraft}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
          data-testid="create-will-btn"
          data-tour-id="tour-create-will"
        >
          {creatingDraft ? (
            <>
              <Loader2 size={18} className="mr-2 animate-spin" /> Creating Draft...
            </>
          ) : (
            <>
              <Plus size={18} className="mr-2" /> Create New Will
            </>
          )}
        </Button>
      </div>

      {/* Wills List or Empty State */}
      {wills.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="p-6 rounded-full bg-slate-800/50">
                <FileText className="w-16 h-16 text-slate-600" />
              </div>
              <h2 className="text-2xl font-bold text-white">No Wills Found</h2>
              <p className="text-slate-400 max-w-md">
                You have not created any Wills yet. Start by creating your first legally valid Will in minutes.
              </p>
              <Button
                onClick={handleCreateWill}
                disabled={creatingDraft}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold mt-4"
              >
                {creatingDraft ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" /> Creating Draft...
                  </>
                ) : (
                  <>
                    <Plus size={18} className="mr-2" /> Create My First Will
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {wills.map((will) => {
            const statusConfig = getStatusConfig(will.status);
            const StatusIcon = statusConfig.icon;
            const currentStep = will.progress?.current_step || 1;
            const totalSteps = 9;
            const progressPercent = Math.round((currentStep / totalSteps) * 100);
            
            // Expiry info
            const isExpired = will.is_expired;
            const daysRemaining = will.days_remaining;
            const expiryDateFormatted = will.expiry_date_formatted;
            const showExpiryWarning = daysRemaining !== null && daysRemaining <= 30 && !isExpired;
            const isUnpaid = !will.is_paid && will.status !== 'completed';

            return (
              <Card 
                key={will.id} 
                className={`bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all cursor-pointer ${isExpired ? 'border-red-500/30' : ''} ${isUnpaid ? 'border-amber-500/20' : ''}`}
                onClick={() => handleEditWill(will.id)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="p-3 rounded-xl bg-slate-800/50">
                      <FileText className={`w-8 h-8 ${isExpired ? 'text-red-400' : isUnpaid ? 'text-amber-400/60' : 'text-amber-400'}`} />
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusConfig.bgColor} ${statusConfig.textColor} ${statusConfig.borderColor} border`}>
                        {statusConfig.label}
                      </span>
                      {isUnpaid && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/30 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" /> Unpaid
                        </span>
                      )}
                      {isExpired && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/30">
                          Expired
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {will.testator_name || 'Unnamed Will'}
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Created: {new Date(will.created_at).toLocaleDateString()}
                    </p>
                    {will.updated_at && will.updated_at !== will.created_at && (
                      <p className="text-sm text-slate-500">
                        Updated: {new Date(will.updated_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {/* Expiry Info */}
                  {daysRemaining !== null && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${
                      isExpired 
                        ? 'bg-red-500/10 border border-red-500/20' 
                        : showExpiryWarning 
                          ? 'bg-amber-500/10 border border-amber-500/20'
                          : 'bg-slate-800/50'
                    }`}>
                      {isExpired ? (
                        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      ) : (
                        <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        {isExpired ? (
                          <p className="text-red-400 text-sm font-medium">Access expired</p>
                        ) : (
                          <p className={`text-sm font-medium ${showExpiryWarning ? 'text-amber-400' : 'text-slate-300'}`}>
                            {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
                          </p>
                        )}
                        <p className="text-xs text-slate-500">Expires: {expiryDateFormatted}</p>
                      </div>
                    </div>
                  )}

                  {/* Progress Bar */}
                  {!isExpired && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Progress</span>
                        <span className={statusConfig.textColor}>Step {currentStep} of {totalSteps}</span>
                      </div>
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${will.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'}`}
                          style={{ width: `${will.status === 'completed' ? 100 : progressPercent}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    {isExpired ? (
                      <>
                        <Button
                          size="sm"
                          className="flex-1 bg-slate-700 text-slate-300 hover:bg-slate-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditWill(will.id);
                          }}
                          data-testid={`view-expired-will-${will.id}`}
                        >
                          <Eye size={14} className="mr-1" /> View (Read-Only)
                        </Button>
                        <Button
                          size="sm"
                          className="bg-amber-500 text-slate-900 hover:bg-amber-400 font-semibold"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRenewWill(will);
                          }}
                          data-testid={`renew-will-${will.id}`}
                        >
                          <Clock size={16} className="mr-1" /> Renew
                        </Button>
                      </>
                    ) : showExpiryWarning ? (
                      <>
                        <Button
                          size="sm"
                          className="flex-1 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditWill(will.id);
                          }}
                        >
                          <Edit size={14} className="mr-1" /> {will.status === 'draft' ? 'Continue' : 'View'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRenewWill(will);
                          }}
                        >
                          <Clock size={14} className="mr-1" /> Renew
                        </Button>
                      </>
                    ) : will.status === 'draft' ? (
                      <Button
                        size="sm"
                        className="flex-1 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditWill(will.id);
                        }}
                      >
                        <Edit size={14} className="mr-1" /> Continue Editing
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="flex-1 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/30"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditWill(will.id);
                        }}
                      >
                        View Will
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteWill(will.id);
                      }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* How It Works */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-white">How It Works</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: '1',
                title: 'Answer Questions',
                description: 'Simple questions about your assets and beneficiaries',
              },
              {
                step: '2',
                title: 'Review & Customize',
                description: 'Review your will and make any necessary changes',
              },
              {
                step: '3',
                title: 'Sign & Witness',
                description: 'Get your will signed and witnessed to make it legal',
              },
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-slate-800/30 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-amber-400 font-bold text-xl">{item.step}</span>
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Payment Modal - now used for paying for existing draft will */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
        subdomain={subdomain}
        initialPrice={partnerPricing?.will_price}
      />

      {/* Renewal Payment Modal */}
      <RenewalPaymentModal
        isOpen={showRenewalModal}
        onClose={() => {
          setShowRenewalModal(false);
          setRenewalWill(null);
        }}
        onSuccess={handleRenewalSuccess}
        willId={renewalWill?.id}
        willName={renewalWill?.testator_name || 'Your Will'}
        initialPrice={partnerPricing?.renewal_price}
      />

      {/* Draft Limit Modal - shown when user has too many unpaid drafts */}
      <DraftLimitModal
        isOpen={showDraftLimitModal}
        onClose={() => setShowDraftLimitModal(false)}
        onDelete={handleDeleteDraftFromLimit}
        drafts={wills.filter(w => !w.is_paid && w.status !== 'completed')}
        maxDrafts={maxUnpaidDrafts}
      />
    </div>
  );
};

export default ClientWill;
